// Milepost strip chart: deficiency metric along the corridor with crash
// positions overlaid. Recharts ComposedChart (style constants mirror
// UsageDashboard.jsx's conventions — copied, not imported, per house style).
import React, { useMemo } from 'react';
import {
  ComposedChart, Bar, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ResponsiveContainer,
} from 'recharts';

const AXIS_TICK = { fill: '#94a3b8', fontSize: 12 };
const TOOLTIP_STYLE = {
  backgroundColor: '#09101b',
  border: '1px solid #334155',
  borderRadius: '0.5rem',
  color: '#e2e8f0',
};

const METRIC_LABEL = {
  SSD: 'speed deficit (mph)',
  PSD: 'speed deficit (mph)',
  GLR: 'glare minutes / yr',
  FSA: 'cross slope (%)',
  HCA: 'curve safety score (%)',
  GRW: 'embankment height (ft)',
  ISD: 'required ISD (ft)',
  ALL: 'deficiency runs',
};
const ANALYSIS_RUN_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c',
  GRW: '#5f3dc4', ISD: '#0ca678',
};

export default function StripChart({ analysisByDirection, crashes, runs, filters, corridorExtent }) {
  const analysis = filters.analysis;
  const isAll = analysis === 'ALL';
  const data = useMemo(() => {
    if (isAll) {
      // ALL mode has no single metric: draw an invisible baseline so the axes
      // render; the runs' ReferenceAreas + crash scatter carry the content.
      return (corridorExtent || [0, 1]).map((mp) => ({ mp, value: 0 }));
    }
    const byMp = new Map();
    for (const [dir, payload] of Object.entries(analysisByDirection || {})) {
      if (!payload) continue;
      if (filters.direction !== 'both' && dir !== filters.direction) continue;
      for (const bin of payload.strip?.bins || []) {
        if (filters.mpRange && (bin.mp < filters.mpRange[0] || bin.mp > filters.mpRange[1])) continue;
        const prev = byMp.get(bin.mp);
        // keep the worse value across directions (negative-is-worse for
        // SSD/PSD/FSA cross slope; GLR positive-is-worse; HCA lower-is-worse)
        const worse = prev === undefined
          ? bin.value
          : analysis === 'GLR' ? Math.max(prev, bin.value) : Math.min(prev, bin.value);
        byMp.set(bin.mp, worse);
      }
    }
    return [...byMp.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([mp, value]) => ({
        mp,
        value: analysis === 'GLR' || analysis === 'HCA' ? value : Math.abs(value),
        raw: value,
      }));
  }, [analysisByDirection, filters, analysis, isAll, corridorExtent]);

  const crashPoints = useMemo(
    () => (crashes || []).map((c) => ({ mp: c.milepost, y: 0, severityClass: c.severityClass })),
    [crashes]
  );

  const domain = corridorExtent
    ? [Math.floor(corridorExtent[0]), Math.ceil(corridorExtent[1])]
    : ['auto', 'auto'];

  if (!data.length && !crashPoints.length) {
    return <div className="safety-strip-empty">No strip data for this selection.</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
        <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
        <XAxis dataKey="mp" type="number" domain={domain} tick={AXIS_TICK}
          label={{ value: 'Milepost', position: 'insideBottomRight', offset: -2, fill: '#64748b', fontSize: 11 }} />
        <YAxis tick={AXIS_TICK} width={44}
          label={{ value: METRIC_LABEL[analysis], angle: -90, position: 'insideLeft', fill: '#64748b', fontSize: 11 }} />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          formatter={(v, name) => (name === 'crash' ? ['crash', ''] : [v, METRIC_LABEL[analysis]])}
          labelFormatter={(mp) => `MP ${Number(mp).toFixed(2)}`}
        />
        {(runs || []).map((run) => (
          <ReferenceArea key={`${run.analysis || analysis}-${run.direction}-${run.id}`}
            x1={run.mpStart} x2={Math.max(run.mpEnd, run.mpStart + 0.01)}
            fill={isAll ? (ANALYSIS_RUN_COLORS[run.analysis] || '#c92a2a') : '#c92a2a'}
            fillOpacity={isAll ? 0.28 : 0.12} />
        ))}
        {!isAll && <Bar dataKey="value" fill="#e8590c" barSize={2} isAnimationActive={false} />}
        <Scatter name="crash" data={crashPoints} dataKey="y" fill="#e03131"
          shape={(props) => (
            <circle cx={props.cx} cy={12} r={3.2}
              fill={props.payload.severityClass === 'pdo' ? '#868e96'
                : props.payload.severityClass === 'unknown' ? '#adb5bd' : '#e03131'} />
          )} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
